import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DocumentType } from '../../../models/DocumentType';

@Injectable({
    providedIn: 'root'
})
export class NewInnovationRequestService {

    isFinishingOTP = false;
    myForm: FormGroup;

    requestorType: string;
    requestType: string;
    selectedDocumentType: DocumentType = 'DNI';

    constructor(private readonly fb: FormBuilder) {
        this.myForm = this.fb.group({});
    }

    resetRequestorAndRequestTypes(): void {
        this.requestorType = '';
        this.requestType = '';
        this.selectedDocumentType = 'DNI';
    }

    futureDateValidator(field: string): ValidatorFn {
        return (): ValidationErrors | null => {
            const today = new Date();
            const inputDate = this.myForm?.get(field)?.value;

            if (inputDate >= today) {
                return { futureDateValidator: true };
            }
            return null;
        };
    }

    setForm(requestorType: string, requestType: string): void {
        this.requestorType = requestorType;
        this.requestType = requestType;
        this.selectedDocumentType = 'DNI'; // Siempre inicia en DNI

        switch (requestorType) {
            case 'PhysicalPerson':
                switch (requestType) {
                    case 'Clave':
                        this.myForm = this.fb.group({
                            firstName: ['', [Validators.required, Validators.minLength(2)]],
                            firstSurname: ['', [Validators.required, Validators.minLength(2)]],
                            secondSurname: [''],
                            personId: ['', [Validators.required, this.documentValidator('DNI')]],
                            birthDate: [''],
                            supportNumber: [''],
                            phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{9}$')]],
                            email: ['', [Validators.email]],
                            language: ['es', [Validators.required]]
                        });
                        break;
                }
                break;
            case 'LegalPerson':
                switch (requestType) {
                    case 'ClientCertificate':
                        this.myForm = this.fb.group({
                            companyName: ['', [Validators.required, Validators.minLength(2)]],
                            companyId: ['', [Validators.required, Validators.pattern('^[A-Z0-9]{9}$')]],
                            firstName: ['', [Validators.required, Validators.minLength(2)]],
                            firstSurname: ['', [Validators.required, Validators.minLength(2)]],
                            secondSurname: [''],
                            phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{9}$')]],
                            email: ['', [Validators.required, Validators.email]],
                            language: ['es', [Validators.required]]
                            // personIdFrontPhoto: ['', [Validators.required]],
                            // personIdBackPhoto: ['', [Validators.required]]
                        });
                        break;
                    case 'VideoId':
                        // TODO: Añadir campos del formulario para Video ID (es posible que esta opción no se implemente)
                        this.myForm = this.fb.group({});
                        break;
                }
                break;
        }
    }

    updateDocumentValidators(documentType: DocumentType): void {
        this.selectedDocumentType = documentType;

        const personIdControl = this.myForm.get('personId');
        const birthDateControl = this.myForm.get('birthDate');
        const supportNumberControl = this.myForm.get('supportNumber');

        if (!personIdControl || !birthDateControl || !supportNumberControl) {
            return;
        }

        // Reset document-related fields
        personIdControl.setValue('');
        birthDateControl.setValue('');
        supportNumberControl.setValue('');

        // Update validators based on document type
        personIdControl.setValidators([Validators.required, this.documentValidator(documentType)]);

        if (documentType === 'DNI') {
            birthDateControl.clearValidators();
            supportNumberControl.clearValidators();
        } else {
            // Keep NIE branch for future behavior changes; currently matches DNI behavior.
            birthDateControl.clearValidators();
            supportNumberControl.clearValidators();
        }

        // Update validity
        personIdControl.updateValueAndValidity();
        birthDateControl.updateValueAndValidity();
        supportNumberControl.updateValueAndValidity();
    }

    resetSidebar(): void {
        this.requestorType = '';
        this.requestType = '';
        this.selectedDocumentType = 'DNI';
        this.myForm = this.fb.group({});
    }

    // #region Document Validation
    documentValidator(documentType: DocumentType): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;

            let valid = false;
            if (documentType === 'DNI') {
                valid = this.validateDni(value);
            } else {
                valid = this.validateNie(value);
            }

            return valid ? null : { documentValidator: true };
        };
    }

    private validateDni(value: string): boolean {
        value = value.toUpperCase();
        if (!/^[0-9]{8}[A-Z]$/.test(value)) {
            return false;
        }
        return this.validateNif(value);
    }

    private validateNie(value: string): boolean {
        value = value.toUpperCase();
        if (!/^[XYZ][0-9]{7}[A-Z]$/.test(value)) {
            return false;
        }

        const niePrefix: Record<string, number> = { X: 0, Y: 1, Z: 2 };
        const nie = value.replace(/[XYZ]/, (letter) => niePrefix[letter].toString());
        return this.validateNif(nie);
    }

    private validateNif(value: string): boolean {
        const letter = value.charAt(8);
        const numbers = parseInt(value.slice(0, 8), 10);
        const validLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
        return letter === validLetters.charAt(numbers % 23);
    }

    // Legacy validator - kept for compatibility
    nifNieValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;

            const valid = this.validateNifNie(value);
            return valid ? null : { nifNieValidator: true };
        };
    }

    validateNifNie(value: string): boolean {
        value = value.toUpperCase();

        if (/^[0-9]{8}[A-Z]$/.test(value)) {
            return this.validateDni(value);
        } else if (/^[XYZ][0-9]{7}[A-Z]$/.test(value)) {
            return this.validateNie(value);
        } else {
            return false;
        }
    }
    // #endregion
}
